import React, { type ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import Card from 'components/common/card';
import type { ColumnProps } from 'components/common/table';
import Table from 'components/common/table';
import SaveDiscardButtons from 'components/common/save-discard-buttons';
import SearchBar from 'components/common/search-bar';
import { useAsyncPromise } from 'hooks/use-async-promise';
import useStores from 'hooks/use-stores.hooks';
import ErrorContainer from 'components/common/error-container';
import styled from 'types/theme.types';
import { NotificationTypes } from 'types/notifications.types';
import { typeStyle } from 'constants/typography';
import type { TableProps, TablePaginationConfig } from 'antd';
import { useIntl } from 'react-intl';

type RoleAssignmentsCardStatus = 'LOADING_DATA' | 'READY' | 'SAVING_DATA';

export type LoadMoreOptions = {
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  /** Rows the server has returned so far; read alongside isFetchingNextPage, ahead of `rows` catching up. */
  loadedCount: number;
  fetchNextPage: () => Promise<unknown>;
};

const PAGE_SIZE_OPTIONS = ['10', '25', '50', '100'];

interface RoleAssignmentsCardProps {
  rows: any[];
  columns: ColumnProps[];
  getRowKey: (row: any) => string;
  onDiscard: () => void;
  onSave: () => Promise<void>;
  loadRows?: () => Promise<void>;
  onFilter?: (searchTerm?: string) => Promise<void> | void;
  filterError?: string | null;
  cardHeader: ReactNode;
  filterPlaceholderId: string;
  /** The primary action beside the search on the hoisted header row (298:41798). */
  headerAction?: ReactNode;
  additionalFooter?: ReactNode;
  /** Pages over the loaded rows and fetches the next server batch once the user reaches the last loaded page. */
  loadMore?: LoadMoreOptions;
  hasChanges?: boolean;
  showFooter?: boolean;
  isLoading?: boolean;
  scroll?: TableProps<any>['scroll'];
}

const RoleAssignmentsCard: React.FunctionComponent<RoleAssignmentsCardProps> = ({
  rows,
  columns,
  getRowKey,
  onDiscard,
  onSave,
  loadRows,
  onFilter,
  cardHeader,
  filterPlaceholderId,
  headerAction,
  additionalFooter,
  loadMore,
  hasChanges = false,
  showFooter = true,
  isLoading = false,
  filterError,
  scroll,
  ...props
}) => {
  const intl = useIntl();
  const { notificationStore } = useStores();
  const [cardStatus, setCardStatus] = useState<RoleAssignmentsCardStatus>('READY');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  // A search or filter shrinks the list; loading more only grows it.
  const previousRowCount = useRef(rows.length);
  useEffect(() => {
    if (rows.length < previousRowCount.current) setCurrentPage(1);
    previousRowCount.current = rows.length;
  }, [rows.length]);

  const isOnLastLoadedPage = !!loadMore && currentPage >= Math.ceil(loadMore.loadedCount / pageSize);
  // Primitives, not the loadMore object: callers rebuild it every render, which would re-run the effect and fetch
  // twice before isFetchingNextPage turns true.
  const { hasNextPage, isFetchingNextPage, fetchNextPage } = loadMore ?? {};
  useEffect(() => {
    // Fetching ahead on the last loaded page enables › and adds the next pages as the batch lands.
    if (isOnLastLoadedPage && hasNextPage && !isFetchingNextPage) void fetchNextPage?.();
  }, [isOnLastLoadedPage, hasNextPage, isFetchingNextPage, fetchNextPage]);

  const loadMorePagination: TablePaginationConfig | undefined = loadMore && {
    current: currentPage,
    pageSize,
    total: rows.length,
    showSizeChanger: true,
    pageSizeOptions: PAGE_SIZE_OPTIONS,
    hideOnSinglePage: false,
    onChange: (page, size) => {
      setCurrentPage(size === pageSize ? page : 1);
      setPageSize(size);
    },
    showTotal: (total, [from, to]) =>
      intl.formatMessage(
        { id: 'table.pagination.range' },
        { from, to, total, more: loadMore.hasNextPage ? 'true' : 'false' }
      )
  };
  // Hands antd only the current page, so it treats the pager as server-driven and does not re-slice.
  const tableRows = loadMore ? rows.slice((currentPage - 1) * pageSize, currentPage * pageSize) : rows;

  const invokeWithCardStatus = useCallback(
    async (status: RoleAssignmentsCardStatus, fn: () => Promise<void> | void, ...args: any[]) => {
      try {
        setCardStatus(status);
        await fn.apply(this, args as []);
      } finally {
        setCardStatus('READY');
      }
    },
    []
  );
  const onSaveClick = async () => {
    try {
      await invokeWithCardStatus('SAVING_DATA', onSave);
    } catch (error) {
      notificationStore.setNotification({
        notificationType: NotificationTypes.error,
        message: intl.formatMessage({ id: 'team.role.assignments.failed' })
      });
    }
  };
  const onTableFilter = async (searchTerm?: string) => {
    if (onFilter) {
      await invokeWithCardStatus('LOADING_DATA', onFilter, searchTerm);
    }
  };

  const [loadingInitialDataError] = useAsyncPromise(async () => {
    if (loadRows) {
      await invokeWithCardStatus('LOADING_DATA', loadRows);
    }
  }, [loadRows, invokeWithCardStatus]);

  const footer = () =>
    showFooter ? (
      <SaveDiscardButtons
        isInline
        saveButton={{
          'data-e2e': 'save-role-changes',
          onClick: onSaveClick,
          disabled: cardStatus === 'SAVING_DATA' || !hasChanges,
          isLoading: cardStatus === 'SAVING_DATA'
        }}
        discardButton={{
          'data-e2e': 'discard-role-changes',
          onClick: onDiscard,
          disabled: cardStatus === 'SAVING_DATA' || !hasChanges
        }}
      />
    ) : undefined;

  if (loadingInitialDataError) {
    return <ErrorContainer errorToReport={loadingInitialDataError} />;
  }

  // Figma 298:41798 hoists the title and description above the card, with the search and the pane's
  // primary action on their row; the card keeps the table and the save row.
  return (
    <PaneStack>
      <PaneHeader>
        <HeaderText>{cardHeader}</HeaderText>
        <HeaderRight>
          {/* The pane's add-action is a header concern (295:27002 puts primary actions on the page/pane
              header), so the footer keeps only the save/discard row. */}
          {additionalFooter}
          {headerAction}
        </HeaderRight>
      </PaneHeader>
      <Card {...props}>
        {onFilter && (
          <HeaderSearchBar
            allowClear
            placeholder={filterPlaceholderId}
            onFilter={onTableFilter}
            disabled={hasChanges}
            error={filterError}
            // Mounted only while there is an error, so the table sits flush under the search otherwise.
            hideError={!filterError}
          />
        )}
        <StyledTable
          scroll={scroll}
          dataSource={tableRows}
          columns={columns}
          rowKey={getRowKey}
          paginated
          isLoading={cardStatus === 'LOADING_DATA' || isLoading}
          pagination={loadMorePagination}
        />
        {footer()}
      </Card>
    </PaneStack>
  );
};

const PaneStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-lg);
`;

const PaneHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--spacing-lg);
`;

const HeaderText = styled.div`
  flex: 1;
  min-width: 0;
  max-width: 660px;

  /* The hoisted title reads as the pane's H2 (298:41800); the stable hook is the title container's
     test id - react-intl swallows the message's own. */
  [data-e2e='card-header-title'] {
    ${typeStyle.headingH2}
    color: var(--text-heading);
  }
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
  flex-shrink: 0;
  padding-left: var(--padding-xxl);
`;

const HeaderSearchBar = styled(SearchBar)`
  width: 250px;
`;

const StyledTable = styled(Table)`
  min-height: 300px;
`;

export default RoleAssignmentsCard;
